/* eslint-disable complexity */

import React, { useCallback, useEffect, useState } from 'react';
import { Dispatch } from 'redux';
import { connect } from 'react-redux';
import { Route, Routes, useParams } from 'react-router-dom';
import get from 'lodash/get';

import { PaymentDetailsContainer } from '@futrli/billing';
import { withErrorBoundary, WithErrorBoundaryProps } from '@futrli/errors';
import { ConsolidationWrapper } from '@futrli/organisations';
import { PredictSettingsContainer } from '@futrli/predict';
import { WorkspacesListingItem } from '@futrli-sdk/workspaces';
import {
  ROUTES,
  getWorkspaceConsolidationsById,
  markAsStale,
  joinRouteMatchPath
} from '@futrli/shared-services';
import { getAccountMetadata, METADATA_FIELD, updateVisitorAccount } from '@futrli/tracking';
import { UIDefaultErrorMessage, UILoader } from '@futrli/ui-components';

import { ProductsManagementRouter } from '../../products/components/products-management-router';
import { useOnboardingUrlInfo } from '../../../../predict/src/forecast/onboarding/utils/hooks';
import { InitialOnboardingLoader } from '../../onboarding/components/initial-onboarding-nav';
import { useCurrentUserId } from '../../../../predict/src/dashboard/hooks';

interface DispatchToProps {
  getConsolidations: (workspaceId: string) => typeof getWorkspaceConsolidationsById;
  markConsolidationsStale: () => typeof markAsStale;
}

interface StateToProps {
  readonly workspaces: WorkspacesListingItem[] | null;
}

type Props = DispatchToProps & WithErrorBoundaryProps & StateToProps;

export const WorkspaceContainerBase = ({
  getConsolidations,
  markConsolidationsStale,
  onManageError,
  workspaces
}: Props) => {
  const { workspaceId } = useParams();
  const { isOnboardingRoute, onboardingForecastId } = useOnboardingUrlInfo();

  const userId = useCurrentUserId();
  const [isLoading, setIsLoading] = useState(true);

  const loadData = useCallback(async () => {
    try {
      if (workspaceId) {
        setIsLoading(true);
        await markConsolidationsStale();
        await getConsolidations(workspaceId);
      }
    } catch (error) {
      onManageError(error);
    } finally {
      setIsLoading(false);
    }
  }, [getConsolidations, markConsolidationsStale, onManageError, workspaceId]);

  useEffect(() => {
    if (workspaceId) {
      loadData();
    }
  }, [loadData, workspaceId]);

  useEffect(() => {
    // Update pendo account (workspace) if the current workspace is different than
    // saved pendo workspace (account.id)
    if (workspaceId && workspaces) {
      if (
        workspaceId !== window.pendo?.accountId ||
        (workspaceId === window.pendo?.accountId && !get(getAccountMetadata(), METADATA_FIELD.SUBSCRIPTION_STATUS))
      ) {
        const w = workspaces.find(w => w.id === workspaceId);
        if (w) {
          updateVisitorAccount(workspaceId, w.subscription_state);
        }
      }
    }
  }, [workspaceId, workspaces, userId]);

  if (isLoading) {
    if (isOnboardingRoute || onboardingForecastId) {
      return <InitialOnboardingLoader />;
    }
    return <UILoader isFull={true} />;
  }

  if (workspaceId) {
    return (
      <Routes>
        <Route path={joinRouteMatchPath(ROUTES.PAYMENT_DETAILS, '/*')} element={<PaymentDetailsContainer />} />
        <Route
          path={joinRouteMatchPath(
            ROUTES.WORKSPACE_BY_ID_SETTINGS_ROUTE,
            ROUTES.CONSOLIDATIONS_ROUTE,
            ROUTES.CREATE_CONSOLIDATION,
            ROUTES.CONSOLIDATION_TAB
          )}
          element={<ConsolidationWrapper />}
        />
        <Route
          path={joinRouteMatchPath(
            ROUTES.WORKSPACE_BY_ID_SETTINGS_ROUTE,
            ROUTES.CONSOLIDATIONS_ROUTE,
            ROUTES.CONSOLIDATION_BY_ID,
            ROUTES.CONSOLIDATION_TAB
          )}
          element={<ConsolidationWrapper />}
        />
        <Route
          path={joinRouteMatchPath(ROUTES.WORKSPACE_BY_ID_SETTINGS_ROUTE, '/*')}
          element={<PredictSettingsContainer />}
        />
        <Route
          path={joinRouteMatchPath(ROUTES.ORGANISATIONS_ROUTE, ROUTES.ORGANISATION_BY_ID_ROUTE, '/*')}
          element={<ProductsManagementRouter />}
        />
        <Route
          path={joinRouteMatchPath(ROUTES.CONSOLIDATIONS_ROUTE, ROUTES.CONSOLIDATION_BY_ID, '/*')}
          element={<ProductsManagementRouter isConsolidation />}
        />
      </Routes>
    );
  }

  return <UIDefaultErrorMessage />;
};

// Redux
const mapStateToProps = () => ({});
const mapDispatchToProps = (dispatch: Dispatch): DispatchToProps => ({
  getConsolidations: (workspaceId: string) => dispatch(getWorkspaceConsolidationsById(workspaceId)),
  markConsolidationsStale: () => dispatch(markAsStale('Consolidation'))
});

export const WorkspaceContainerBaseWE = withErrorBoundary(WorkspaceContainerBase);
export const WorkspaceContainer = connect(mapStateToProps, mapDispatchToProps)(WorkspaceContainerBaseWE);
